import React, { FC } from 'react';
import { Alert, Badge, Button, Card, Spinner } from 'react-bootstrap';
import { useTranslation } from 'react-i18next';
import { AliasConflict } from './dto/EntityAlias';

interface Props {
  conflicts: AliasConflict[];
  loading: boolean;
  onFix: (surface: string) => void;
}

/**
 * Words that name more than one thing, most-asked first.
 *
 * This is the part of the screen worth opening first. It is ranked by how often the bot
 * actually had to stop and ask over the last ninety days, so the top of the list is the
 * vocabulary costing real turns rather than whichever collision someone noticed.
 */
export const AliasConflicts: FC<Props> = ({ conflicts, loading, onFix }) => {
  const { t } = useTranslation();

  if (loading) {
    return (
      <div className="text-center py-4">
        <Spinner animation="border" />
      </div>
    );
  }

  if (conflicts.length === 0) {
    return <Alert variant="success">{t('No word currently names more than one thing.')}</Alert>;
  }

  return (
    <div className="d-flex flex-column gap-2 mb-4">
      {conflicts.map((conflict) => (
        <Card key={`${conflict.entityType}:${conflict.surface}`}>
          <Card.Body className="d-flex align-items-center justify-content-between flex-wrap gap-2">
            <div>
              <strong>{conflict.surface}</strong>{' '}
              {conflict.timesAsked > 0 && (
                <Badge bg="warning" text="dark">
                  {t('asked {{count}} times', { count: conflict.timesAsked })}
                </Badge>
              )}
              <div className="text-muted small mt-1">
                {conflict.entities.map((entity) => entity.canonical).join('  •  ')}
              </div>
            </div>

            <Button size="sm" variant="outline-primary" onClick={() => onFix(conflict.surface)}>
              {t('Add a name for this')}
            </Button>
          </Card.Body>
        </Card>
      ))}
    </div>
  );
};
